import { Injectable, computed, inject, signal } from '@angular/core';
import { ApprovalEngineService } from '@/modules/settings/components/approval-engine/services/approval-engine.service';
import { PendingApprovalStep } from '@/modules/settings/components/approval-engine/models/approval-engine.models';

const POLL_INTERVAL_MS = 60_000;

// Mapeo EntityType (del motor de aprobaciones) → id de módulo (module-home), para
// mostrar el badge de pendientes sobre el ícono del módulo correcto.
//
// `PurchaseOrder` queda deliberadamente afuera: las órdenes de compra se crean en Concreto y su
// única superficie en Sym70 es la ruta global /approvals, no un módulo. Colgarlas de uno mandaría
// al aprobador a una pantalla donde no hay nada que firmar. Igual suman al totalCount de la campana.
const ENTITY_TYPE_TO_MODULE_ID: Record<string, string> = {
    Nomina: 'talento-humano',
    PayrollPeriod: 'talento-humano',
};

/**
 * Servicio global de aprobaciones pendientes (campanita del topbar + badge de módulos).
 * A diferencia de PendingCountPollerService (que solo arranca al visitar una pantalla
 * puntual de Talento Humano), este se auto-inicia en el constructor — es `providedIn:
 * 'root'`, así que cualquier componente que lo inyecte (topbar, module-home) dispara
 * el primer refresh y el polling, sin depender de que alguien llame a un método aparte.
 */
@Injectable({ providedIn: 'root' })
export class ApprovalPendingService {
    private readonly engine = inject(ApprovalEngineService);

    private readonly _steps = signal<PendingApprovalStep[]>([]);
    readonly steps = this._steps.asReadonly();

    readonly totalCount = computed(() => this._steps().length);

    // Conteo de pendientes por id de módulo (solo módulos con entradas en el mapeo).
    readonly countByModule = computed(() => {
        const map = new Map<string, number>();
        for (const step of this._steps()) {
            const moduleId = ENTITY_TYPE_TO_MODULE_ID[step.entityType];
            if (!moduleId) continue;
            map.set(moduleId, (map.get(moduleId) ?? 0) + 1);
        }
        return map;
    });

    private timer: ReturnType<typeof setInterval> | null = null;

    constructor() {
        //TODO: Move this to suprsend notifications
        this.refresh();
        this.timer = setInterval(() => this.refresh(), POLL_INTERVAL_MS);
    }

    /** Refresca la lista desde el backend. Útil tras aprobar/rechazar un paso. */
    refresh(): void {
        this.engine.getMyPendingSteps().subscribe({
            next: steps => this._steps.set(steps ?? []),
            error: () => {
                // 401/403/red — no romper, deja el estado anterior.
            },
        });
    }

    pendingCountForModule(moduleId: string): number {
        return this.countByModule().get(moduleId) ?? 0;
    }
}
